import { Button, Skeleton, View } from "@/components/ui";
import type { ServiceProfileType } from "../lib";
import { useClientServiceProfileByFiltersQuery } from "@/gqlhooks/profile/useClientServiceProfileByFiltersQuery";
import { Typography } from "@/components/shared/typography";
import { CreateNewProfileCard } from "@/components/shared/service-profile/create-new-profile-card";
import { useRoute } from "@/components/shared/router";
import { ServiceProfileCard } from "@/components/shared/service-profile/service-profile-card";
import { useDeleteClientServiceProfileMutation } from "@/gqlhooks/profile/useDeleteClientServiceProfileMutation";
import { useUpdateClientServiceProfileMutation } from "@/gqlhooks/profile/useUpdateClientServiceProfileMutation";
import { LoadingDialog } from "@/components/shared/loading-dialog";
import { showToast } from "@/components/ui/toast/show-toast";
import { getErrorMessage } from "@/lib/helpers/string";

export const ServiceProfileList = ({ type }: { type?: ServiceProfileType }) => {
  const { profiles, defaultProfile, loading, refetch } =
    useClientServiceProfileByFiltersQuery(type);
  const [deleteProfile, { loading: deleteProfileLoading }] =
    useDeleteClientServiceProfileMutation();
  const [updateProfile, { loading: updateProfileLoading }] =
    useUpdateClientServiceProfileMutation();

  const { push } = useRoute();

  const handleCreate = (type: ServiceProfileType) => {
    push({
      pageKey: "serviceProfileCreate",
      params: {
        type,
      },
    });
  };

  const handleViewMore = (type: ServiceProfileType) => {
    push({
      pageKey: "serviceProfileType",
      params: {
        type,
      },
    });
  };

  const handleEdit = (id: string, type: ServiceProfileType) => {
    push({
      pageKey: "serviceProfileEdit",
      params: { id, type },
    });
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await deleteProfile({
        variables: {
          input: { id },
        },
        onCompleted: () => {
          refetch();
        },
      });

      if (res.data?.deleteClientServiceProfile?.result) {
        showToast({
          title: "Success delete service profile",
          type: "success",
        });
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Failed to delete service profile");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const res = await updateProfile({
        variables: {
          input: { id, primary: true },
        },
        onCompleted: () => {
          refetch();
        },
      });

      if (res.data?.updateClientServiceProfile) {
        showToast({
          title: "Success set default service profile",
          type: "success",
        });
      }
    } catch (error) {
      const errMsg = getErrorMessage(
        error,
        "Failed to set default service profile",
      );
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  if (loading) {
    return (
      <View className="flex flex-col gap-6 px-4 py-4 md:px-0">
        <View className="flex flex-col gap-3">
          <View className="flex flex-row justify-between gap-2">
            <Skeleton className="h-[16px] w-1/2" />
            <Skeleton className="h-[16px] w-[80px]" />
          </View>
          <Skeleton className="h-[64px] w-full" />
        </View>
      </View>
    );
  }

  if (type) {
    const canCreateNewProfile = () => {
      if (!type) return false;
      if (type === "pet") {
        const catCount = profiles.filter(
          (profile) => profile.visibleAttributes[0] === "Cat",
        ).length;
        const dogCount = profiles.filter(
          (profile) => profile.visibleAttributes[0] === "Dog",
        ).length;
        return catCount < 3 || dogCount < 3;
      }
      return profiles.length < 3;
    };
    return (
      <View className="flex flex-col gap-6 px-4 py-4 md:px-0">
        <View className="flex flex-col gap-3">
          {profiles.map((profile) => (
            <ServiceProfileCard
              key={profile.id}
              profile={profile}
              handleEdit={() => handleEdit(profile.id, type)}
              otherActions={[
                {
                  label: "Set as default",
                  onClick: () => void handleSetDefault(profile.id),
                },
                {
                  label: "Delete profile",
                  render: (label) => (
                    <Typography variant="body-sm" color="danger">
                      {label}
                    </Typography>
                  ),
                  onClick: () => void handleDelete(profile.id),
                },
              ]}
            />
          ))}
          {canCreateNewProfile() && (
            <CreateNewProfileCard onClick={() => handleCreate(type)} />
          )}
          <Typography
            variant="caption-sm"
            align="center"
            color="foreground-intermediate"
          >
            {type === "pet"
              ? "You can have up to 3 cat profiles and 3 dog profiles."
              : "You can have up to 3 profiles."}
          </Typography>
        </View>
        <LoadingDialog
          open={deleteProfileLoading || updateProfileLoading}
          label={
            deleteProfileLoading ? "Deleting profile..." : "Updating profile..."
          }
        />
      </View>
    );
  }

  return (
    <View className="flex flex-col gap-6 px-4 py-4 md:px-0">
      <View className="flex flex-col gap-3">
        <View className="flex flex-row justify-between gap-2">
          <Typography variant="label-sm">Default Pet Profile</Typography>
          <Button
            variant="secondary"
            size="sm"
            color="CTA2"
            onClick={() => handleViewMore("pet")}
          >
            View All
          </Button>
        </View>
        {defaultProfile.pet ? (
          <ServiceProfileCard profile={defaultProfile.pet} />
        ) : (
          <CreateNewProfileCard onClick={() => handleCreate("pet")} />
        )}
      </View>
      <View className="flex flex-col gap-3">
        <View className="flex flex-row justify-between gap-2">
          <Typography variant="label-sm">Default Elder Profile</Typography>
          <Button
            variant="secondary"
            size="sm"
            color="CTA2"
            onClick={() => handleViewMore("elder")}
          >
            View All
          </Button>
        </View>
        {defaultProfile.elder ? (
          <ServiceProfileCard profile={defaultProfile.elder} />
        ) : (
          <CreateNewProfileCard onClick={() => handleCreate("elder")} />
        )}
      </View>
      <View className="flex flex-col gap-3">
        <View className="flex flex-row justify-between gap-2">
          <Typography variant="label-sm">Default Child Profile</Typography>
          <Button
            variant="secondary"
            size="sm"
            color="CTA2"
            onClick={() => handleViewMore("baby")}
          >
            View All
          </Button>
        </View>
        {defaultProfile.baby ? (
          <ServiceProfileCard profile={defaultProfile.baby} />
        ) : (
          <CreateNewProfileCard onClick={() => handleCreate("baby")} />
        )}
      </View>
    </View>
  );
};
